import type {ReactNode} from 'react';

import {LocaleSafeString, StrictTranslatable} from 'common/i18n';

import {ModalType} from '../modal';

export enum THEME {
  CONFIRM = `confirm`,
  INFO = `info`,
  DELETE = `delete`,
  WARN = `warn`,
}

export interface ConfirmAttrs {
  /** whether to hide the cancel button or not */
  'hide-cancel-button'?: boolean;
  /** label for the cancel button */
  'cancel-button-label'?: LocaleSafeString;
  /** mp-modal closeable (x in top right) */
  closeable?: boolean;
  /** whether to disable the confirm button or not */
  'confirm-button-disabled'?: boolean;
  /** label for the confirm button */
  'confirm-button-label'?: LocaleSafeString;
  /** whether the modal is open or not */
  open?: boolean;
  /** title at the top of the modal */
  title?: LocaleSafeString;
  /** text in the body of the modal */
  description?: LocaleSafeString;
  /** allow escape newlines in the description text */
  'description-allow-newline'?: boolean;
  /** use markdown to render the description, mutually exclusive with description-allow-new-line */
  'description-use-markdown'?: boolean;
  /** modal theme (e.g. delete) */
  theme?: THEME;
  /** include an input field in the modal */
  'show-input'?: boolean;
  /** current string value of the input field */
  'initial-input-value'?: string;
  /** allows input field to be empty to submit */
  'allow-empty-input-value'?: boolean;
  /** displays an info banner at the bottom of the modal content */
  'bottom-banner-text'?: LocaleSafeString;
  /** sizes the modal to its content instead of a fixed height */
  'grow-to-content'?: boolean;
  'not-fullscreen'?: boolean;
  'modal-type'?: ModalType.Popup;
  /** the string value for test reference */
  elref?: string;
  /** ARIA role applied to the dialog container */
  role?: string;
  id?: string;
  /** inline style applied to the dialog (e.g. 'width: 600px') */
  style?: string;
}

export interface ConfirmParams {
  confirmButtonLabel?: StrictTranslatable;
  title?: StrictTranslatable;
  description?: StrictTranslatable;
}

export type ValidatorFn = (inputValue: string) => LocaleSafeString | null;

/**
 * The imperative-only bag passed as the second argument to `confirm()` / `confirmDelete()` /
 * `prompt()`. Distinct from the FC's own props in `./component`.
 */
export interface ConfirmImperativeProps {
  inputValidator?: ValidatorFn;
  bannerContent?: ReactNode;
}
